<template>
<div class="trend-related-chart-group" :style="{
  '--related-chart-width': relatedChartWidth
}">
  <dimension-weather-chart
    v-if="hasAllWindFactor"
    ref="chart"
    :data-config="dataConfig"
    :dimension="0"
    :unit-prop="unitProp"
    :download-name="downloadName"
    :chart-height="chartHeight"
  />
  <x-y-chart
    v-else
    ref="chart"
    :data-config="dataConfig"
    :unit-prop="unitProp"
    :chart-type="chartType"
    :stack-type="stackType"
    :download-name="downloadName"
    :chart-height="chartHeight"
    selected-change-with-data-changed
  />

  <div class="related-chart-container">
    <div class="is-more-select">
      <el-select
        v-if="isMore"
        v-model="selectSite"
        multiple
        collapse-tags
        size="small"
        style="width: 200px;"
        v-default-select="[enterpriseIds]"
        @change="resizeLeft"
      >
        <el-option
          v-for="item in filterOption"
          :disabled="item.id === dataConfig.id"
          :key="item.id"
          :value="item.id"
          :label="item.name"
        />
      </el-select>
    </div>
    <div v-if="dimensions.length <= 2" class="related-chart-header">
      <span>{{dimensionMap[selectedDimensionIds[0]].name}}</span>
      <span>VS</span>
      <span>{{dimensionMap[selectedDimensionIds[1]].name}}</span>
    </div>

    <div v-else class="related-chart-header">
      <el-select
        v-model="selectedDimensionIds[0]"
        size="small"
        style="width:120px;"
      >
        <el-option
          v-for="dimension in dimensions"
          :key="dimension.id"
          :value="dimension.id"
          :label="dimension.name"
        />
      </el-select>

      <span>VS</span>

      <el-select
        v-model="selectedDimensionIds[1]"
        size="small"
        style="width:120px;"
      >
        <el-option
          v-for="dimension in dimensions"
          :key="dimension.id"
          :value="dimension.id"
          :label="dimension.name"
        />
      </el-select>
    </div>

    <div v-if="isMore" class="xy-chart-legend-wrapper">
      <ul class="xy-chart-legends" :style="{'max-width': dimension ? '63%' : '70%'}">
        <li
          v-for="(dimension, index) in series"
          :key="index"
          :class="{
            active: selected[dimension.name]
          }"
          @click="onLegendClick(dimension)"
        >
        <span
          class="chart-legend-section"
          :chart-type="dimension.type === 'scatter' ? 'bar' : dimension.type"
          :class="{
            'custom-symbol': false
          }"
          :style="{
            '--chart-legend-section-bg': dimension.color || COLORS[index % COLORS.length]
          }"
        ><i v-if="dimension.type !=='line'"></i></span>
          <span class="chart-legend-name">{{dimension.name}}</span>
        </li>
      </ul>
    </div>

    <div :style="{
      'height': relatedChartWidth
    }">
      <related-chart-v2
        ref="related"
        :is-more="isMore"
        :dimension="dimension"
        :show-legend="!isMore"
        :base-data="isMore ? base : relatedData[0]"
        :related-data="isMore ? related : relatedData[1]"
        :audit-rule-map="auditRuleFlagMap"
        :selected="selected"
        :scale="scale"
      />
    </div>
  </div>
</div>
</template>

<script>
import {mapGetters} from 'vuex'
import calc from './calc'
import XYChart from '../DataChart/XYChart'
import RelatedChartV2 from './RelatedChartV2'
import DimensionWeatherChart from './ChartWrapper/DimensionWeatherChart'

export default {
  name: 'TrendRelatedChartV2',
  components: {DimensionWeatherChart, XYChart, RelatedChartV2},
  props: {
    dataConfig: {
      required: true,
      type: Object
    },
    unitProp: {
      required: true,
      type: String
    },
    chartHeight: {
      type: String,
      default: 'auto'
    },
    downloadName: {
      required: true,
      type: String
    },
    relatedChartWidth: {
      type: String,
      default: '418px'
    },
    clickAble: {
      type: Boolean
    },
    scale: {
      type: Boolean,
      default: false
    },
    dataList: {
      required: true,
      type: Array
    },
    options: {
      required: true,
      type: Array
    },
    isMore: {
      type: Boolean,
      default: false
    },
    filterSiteMap: {
      required: true,
      type: Object
    },
    filterFactorMap: {
      required: true,
      type: Object
    },
    dimension: {
      type: Number
    }
  },
  data () {
    return {
      chartType: 'line',
      stackType: 0,
      selectedDimensionIds: [],
      enterpriseIds: [],
      selectSite: [],
      selected: {},
      series: []
    }
  },
  computed: {
    ...mapGetters({
      auditRuleFlagMap: 'AuditRule/flagMap'
    }),
    COLORS () {
      return [
        '#3ba272',
        '#F44336',
        '#7E57C2',
        '#29B6F6',
        '#26A69A',
        '#8BC34A',
        '#FFEE58',
        '#FF9800',
        '#607D8B',
        '#795548',
        '#EC407A',
        '#AB47BC',
        '#26C6DA',
        '#fe7300',
        '#66BB6A',
        '#D4E157',
        '#FFCA28'
      ]
    },
    hasAllWindFactor () {
      const windFactorIds = this.dataConfig.dimensions.reduce((list, dimension) => {
        const {factorId} = dimension

        if (/^(1012|1013)$/.test(factorId)) {
          list.push(factorId)
        }

        return list
      }, [])

      return windFactorIds.includes('1012') && windFactorIds.includes('1013')
    },
    dimensions () {
      let {dataList, isMore, selectSite} = this
      if (isMore) {
        let data = dataList.map(el => {
          let obj
          selectSite.forEach(item => {
            if (item === el.id) {
              obj = el.dimensions
            }
          })

          return obj
        }).filter(Boolean)
        let computedFactor = data.flat().reduce((map, el) => {
          if (!map[el.id]) {
            map[el.id] = {
              num: 0,
              data: el
            }
          }

          map[el.id].num += 1
          return map
        }, {})

        let temp = Object.values(computedFactor).map(el => {
          if (el.num === data.length) {
            return el.data
          }
        })

        return temp.filter(Boolean)
      } else {
        return this.dataConfig.dimensions
      }
    },
    dimensionMap () {
      return this.dimensions.reduce((map, dimension) => {
        map[dimension.id] = dimension
        return map
      }, {})
    },
    filterOption () {
      let {dimensions, dimensionMap, filterSiteMap, dataConfig, dataList, dimension, filterFactorMap} = this
      if (!dimensions.length) {
        return [filterSiteMap[dataConfig.id]]
      } else {
        let data = dataList.map(el => {
          let temp = el.dimensions.filter(item => dimensionMap[item.id])
          if (temp.length > 0) {
            return dimension === 0 ? filterSiteMap[el.id] : filterFactorMap[el.id]
          }
        }).filter(Boolean)
        return dimension === 0 ? data : data.map(el => {
          return {
            ...el,
            id: el.factorId
          }
        })
      }
    },
    seriesMap () {
      return this.dataConfig.seriesMap
    },
    dataTimes () {
      return this.dataConfig.dataTimes
    },
    dataListMap () {
      return this.dataList.reduce((map, el) => {
        map[el.id] = el
        return map
      }, {})
    },
    relatedData () {
      let {dimensionMap, seriesMap, dataTimes, unitProp, isMore, dataListMap, selectSite, filterSiteMap, filterFactorMap} = this

      if (isMore) {
        let temp = []
        selectSite.forEach(id => {
          let data = dataListMap[id]
          let dimensionMap = data.dimensions.reduce((map, dimension) => {
            map[dimension.id] = dimension
            return map
          }, {})

          let seriesMap = data.seriesMap
          let dataTimes = data.dataTimes
          let series = this.selectedDimensionIds.map((dimensionId, i) => {
            let dimension = dimensionMap[dimensionId]
            let series = seriesMap[dimensionId]

            return {
              key: i === 0 ? 'base' : 'related',
              id: dimensionId,
              siteId: id,
              siteName: this.dimension === 0 ? filterSiteMap[id].name : filterFactorMap[id].name,
              name: dimension.name,
              unit: dimension[unitProp],
              data: dataTimes.map(dataTimeObj => {
                let dataTime = dataTimeObj[1]
                let data = series[dataTime]
                return data || {
                  dataTime,
                  value: null
                }
              })
            }
          })

          temp.push(series)
        })

        return temp.flat()
      } else {
        return this.selectedDimensionIds.map(dimensionId => {
          let dimension = dimensionMap[dimensionId]
          let series = seriesMap[dimensionId]

          return {
            id: dimensionId,
            name: dimension.name,
            unit: dimension[unitProp],
            data: dataTimes.map(dataTimeObj => {
              let dataTime = dataTimeObj[1]
              let data = series[dataTime]
              return data || {
                dataTime,
                value: null
              }
            })
          }
        })
      }
    },
    base () {
      if (this.isMore) {
        return this.relatedData.filter(el => el.key === 'base')
      }

      return {}
    },
    related () {
      if (this.isMore) {
        return this.relatedData.filter(el => el.key === 'related')
      }

      return {}
    }
  },
  methods: {
    setRelatedChart () {
      let {base, related, isMore} = this
      let newBaseDataList = []
      let newRelatedDataList = []

      if (isMore) {
        base.forEach((baseDataItem, index) => {
          let relatedDataItem = related[index]
          let baseDataTemp = []
          let relatedDataTemp = []

          baseDataItem.data.forEach((baseItem, i) => {
            let relatedItem = relatedDataItem.data[i]

            if (!Number.isFinite(baseItem.value) || !Number.isFinite(relatedItem.value)) {
              return
            }

            baseDataTemp.push(baseItem)
            relatedDataTemp.push(relatedItem)
          })

          newBaseDataList.push(baseDataTemp)
          newRelatedDataList.push(relatedDataTemp)
        })
      } else {
        let baseDataList = base.data
        let relatedDataList = related.data

        baseDataList.forEach((baseData, index) => {
          let relatedData = relatedDataList[index]

          if (!Number.isFinite(baseData.value) || !Number.isFinite(relatedData.value)) {
            return
          }

          newBaseDataList.push(baseData)
          newRelatedDataList.push(relatedData)
        })
      }

      if (newBaseDataList.length > 0) {
        this.setSeries(true, newBaseDataList, newRelatedDataList)
      } else {
        this.setSeries(false, newBaseDataList, newRelatedDataList)
      }
    },
    setSeries (flag, xDataList, yDataList) {
      let {base, related, isMore, COLORS} = this
      let COLORS_LEN = COLORS.length
      let series = []

      if (isMore) {
        xDataList.forEach((el, i) => {
          let scatterValues = el.map((xData, index) => [xData.value, yDataList[i][index].value, base[i], related[i], xData, yDataList[i][index]])
          let normalPoints = this.normalPoints = []
          let exceptionPoints = this.exceptionPoints = []
          let zeroPoints = this.zeroPoints = []
          let normalXDataList = []
          let normalYDataList = []

          scatterValues.forEach(data => {
            let xFlag = data[4].flag
            let yFlag = data[5].flag

            if (data[0] === 0 && data[1] === 0) {
              zeroPoints.push(data)
            } else if ((!xFlag || xFlag === 'N' || xFlag === 'RESERVE') && (!yFlag || yFlag === 'N' || yFlag === 'RESERVE')) {
              normalPoints.push(data)
              normalXDataList.push(data[4])
              normalYDataList.push(data[data.length - 1])
            } else {
              exceptionPoints.push(data)
            }
          })

          if (flag) {
            let {a, b, r} = calc.calcRelated(normalXDataList, normalYDataList)
            let tempSeries = [{
              name: `${base[i].siteName}`,
              type: 'scatter',
              color: COLORS[i % COLORS_LEN]
            }, {
              id: 'y',
              name: base[0].name === related[0].name ? (i + 1) + this.getY(a, b, r) : this.getY(a, b, r),
              type: 'line',
              color: COLORS[i % COLORS_LEN]
            }]

            series.push(...tempSeries)
          }
        })
      } else {
        let scatterValues = xDataList.map((xData, index) => [xData.value, yDataList[index].value, base, related, xData, yDataList[index]])
        let normalPoints = this.normalPoints = []
        let exceptionPoints = this.exceptionPoints = []
        let zeroPoints = this.zeroPoints = []
        let normalXDataList = []
        let normalYDataList = []

        scatterValues.forEach(data => {
          let xFlag = data[4].flag
          let yFlag = data[5].flag

          if (data[0] === 0 && data[1] === 0) {
            zeroPoints.push(data)
          } else if ((!xFlag || xFlag === 'N' || xFlag === 'RESERVE') && (!yFlag || yFlag === 'N' || yFlag === 'RESERVE')) {
            normalPoints.push(data)
            normalXDataList.push(data[4])
            normalYDataList.push(data[data.length - 1])
          } else {
            exceptionPoints.push(data)
          }
        })

        if (flag) {
          let {a, b, r} = calc.calcRelated(normalXDataList, normalYDataList)
          series = [{
            name: `${base.name} vs ${related.name}`,
            type: 'scatter'
          }, {
            name: '零值点',
            type: 'scatter',
            color: '#ffff00'
          }, {
            name: '异常点',
            type: 'scatter',
            color: '#ff0000'
          }, {
            id: 'y',
            name: this.getY(a, b, r),
            type: 'line'
          }]
        }
      }

      this.series = Object.freeze(series)
      this.selected = this.buildSelected()
    },
    getY: (a, b, r) => `y=${a.toFixed(3)}x${b < 0 ? '-' : '+'}${Math.abs(b.toFixed(3))},R=${r.toFixed(3)}`,
    buildSelected () {
      return this.series.reduce((map, dimension) => {
        map[dimension.name] = true
        return map
      }, {})
    },
    resizeLeft () {
      let chart = this.$refs.chart.getChart || ''
      if (chart) {
        this.$refs.chart.getChart().chart.resize()
      }
    },
    onLegendClick (dimension) {
      let {selected} = this
      let {name} = dimension
      selected[name] = !selected[name]

      setTimeout(() => {
        this.$refs.related.setRelatedChart()
      }, 10)
    },
    setSelectedDimensionIds (dimensions) {
      this.selectedDimensionIds = [dimensions[0].id, dimensions[Math.max(Math.min(1, dimensions.length - 1), 0)].id]
    },
    onRelatedChartDBLClick (chartData) {
      if (!this.clickAble) {
        return
      }

      let {dataTimes, dimensions, seriesMap, auditRuleFlagMap, dataConfig} = this
      let {dataTime} = chartData[4]
      let index = dataTimes.findIndex(dataTimeObj => dataTimeObj[1] === dataTime)
      let start = Math.max(0, index - 5)
      let end = Math.min(dataTimes.length, start + 11)
      start = Math.max(0, end - 11)

      this.$emit('dblclick', Object.freeze({
        id: dataConfig.id,
        name: dataConfig.name,
        dataTime,
        dataList: Object.freeze(dataTimes.slice(start, end).map(([ts, dataTime]) => dimensions.reduce((map, dimension) => {
          let dimensionId = dimension.id
          let series = seriesMap[dimensionId]
          let data = series[dataTime]

          if (data) {
            let {flag} = data
            map[dimensionId] = `${data.value}${flag ? `(${auditRuleFlagMap[flag] ? auditRuleFlagMap[flag].name : '正常'})` : ''}`
          } else {
            map[dimensionId] = '--'
          }

          return map
        }, {
          dataTime,
          ts
        })))
      }))
    },
    getChartComponent () {
      return this.$refs.chart.getChartComponent()
    }
  },
  watch: {
    dimensions (dimensions) {
      this.setSelectedDimensionIds(dimensions)
    },
    dataConfig: {
      immediate: true,
      handler (data) {
        this.selectSite = [data.id]
        this.enterpriseIds = [data.id]
      }
    },
    base: {
      immediate: true,
      handler (data) {
        if (data.length > 0) {
          this.setRelatedChart()
        }
      }
    }
  },
  created () {
    this.setSelectedDimensionIds(this.dimensions)
  }
}
</script>

<style lang="scss" scoped>
.trend-related-chart-group {
  display: grid;
  grid-template-columns: auto var(--related-chart-width);

  > * {
    min: {
      width: 0;
      height: 0;
    }
  }

  > .related-chart-container {
    display: grid;
    grid-template-rows: max-content 40px max-content auto;
    grid-row-gap: 6px;

    > * {
      min: {
        width: 0;
        height: 0;
      }
    }

    > .is-more-select {
      display: grid;
      grid-template-columns: max-content max-content;
      justify-content: center;
    }

    > .related-chart-header {
      font-size: 22px;
      display: grid;
      grid-template-columns: repeat(3, max-content);
      grid-column-gap: .5em;
      justify-content: center;
      align-items: center;
    }
  }

  .xy-chart-legend-wrapper {
    display: flex;
    justify-content: center;
  }

  .xy-chart-legends {
    font: {
      size: 14px;
      family: '宋体';
    };
    display: flex;
    flex-wrap: wrap;
    max-width: 70%;
    gap: 10px;
    margin: 0;
    padding: 0;
    color: #333333;
    user-select: none;

    > li {
      display: grid;
      grid-template-columns: 25px max-content;
      grid-column-gap: 10px;
      align-items: center;
      height: 1em;
      line-height: 1;
      cursor: pointer;

      &.active {
        > .chart-legend-section {
          &:before,
          &:after {
            background-color: currentColor !important
          }

          &:before,
          &:after,
          > i {
            border-color: currentColor !important;
          }

          &[chart-type="line"] {
            > i {
              opacity: 1 !important;
              filter: grayscale(0) !important;

              &:before {
                background-color: currentColor !important;
              }
            }
          }

          &[chart-type="bar"] {
            > i {
              background-color: currentColor !important;
            }
          }
        }

        > .chart-legend-name {
          color: inherit;
        }
      }

      > .chart-legend-section {
        height: 100%;
        color: var(--chart-legend-section-bg);

        &:before,
        &:after {
          background-color: #cccccc;
        }

        > i {
          height: 100%;
          border: 1px solid #cccccc;
        }

        &[chart-type="bar"] {
          display: flex;

          > i {
            width: 14px;
            border: {
              radius: 999px;
            };
            background-color: #cccccc;
          }
        }

        &[chart-type="line"] {
          display: grid;
          grid-template-columns: 1fr 1em 1fr;
          align-items: center;

          &:before,
          &:after {
            content: "";
            height: 2px;
          }

          &.custom-symbol {
            > i {
              display: flex;
              justify-content: center;
              align-items: center;
              border: 0 {
                radius: 0;
              };
              background: {
                image: var(--chart-legend-custom-symbol);
                position: center;
                size: contain;
                repeat: no-repeat;
              };
              filter: grayscale(1);
              fill-opacity: 50%;
              opacity: .5;

              &:before {
                content: "";
                width: 100%;
                height: 2px;
                background-color: currentColor;
              }
            }
          }
        }
      }

      > .chart-legend-name {
        font-size: 12px;
        color: #ccc;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Data/TrendRelatedChartV2.vue